import { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { History, RefreshCcw, Search } from "lucide-react";

/**
 * AdminAuditLogs: inspector over the `audit_logs` collection.
 * Read-only. Filters by action_type, company_id, user_id, free-text.
 */
export default function AdminAuditLogs() {
  const [filters, setFilters] = useState({ action_type: "", company_id: "", user_id: "", q: "" });
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v.trim()) params.set(k, v.trim()); });
    params.set("limit", "200");
    setLoading(true);
    api.get(`/admin/audit-logs?${params}`)
      .then((r) => setItems(r.data.items || []))
      .finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-audit-logs-page">
      <h1 className="font-display text-2xl font-semibold flex items-center gap-2">
        <History className="w-5 h-5" /> Audit logs
      </h1>
      <p className="text-sm text-slate-600 mt-1 max-w-3xl">
        Every important platform event lands here: admin actions, plan changes,
        review moderation, ticket lifecycle, legal acceptance etc. Read-only.
      </p>

      <div className="mt-4 grid sm:grid-cols-4 gap-2">
        <FieldInline label="Action type" value={filters.action_type} testId="audit-filter-action"
                       placeholder="e.g. admin_company_updated"
                       onChange={(v) => setFilters({ ...filters, action_type: v })} />
        <FieldInline label="Company ID" value={filters.company_id} testId="audit-filter-company"
                       onChange={(v) => setFilters({ ...filters, company_id: v })} />
        <FieldInline label="User ID" value={filters.user_id} testId="audit-filter-user"
                       onChange={(v) => setFilters({ ...filters, user_id: v })} />
        <FieldInline label="Description contains" value={filters.q} testId="audit-filter-q" icon={Search}
                       onChange={(v) => setFilters({ ...filters, q: v })} />
      </div>
      <div className="mt-2 flex items-center gap-2">
        <button onClick={load}
                className="px-3 py-1.5 text-sm rounded-lg border border-slate-300 hover:bg-slate-50 inline-flex items-center gap-1">
          <RefreshCcw className="w-3.5 h-3.5" /> Refresh
        </button>
        <span className="text-xs text-slate-500 ml-auto">
          {loading ? "Loading…" : `${items.length} entries`}
        </span>
      </div>

      <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="text-left p-2">When</th>
              <th className="text-left p-2">Action</th>
              <th className="text-left p-2">Company</th>
              <th className="text-left p-2">User</th>
              <th className="text-left p-2">Description</th>
            </tr>
          </thead>
          <tbody data-testid="audit-logs-tbody">
            {items.map((l) => (
              <tr key={l.id} data-testid={`audit-row-${l.id}`} className="border-t border-slate-100">
                <td className="p-2 text-[11px] text-slate-600 whitespace-nowrap">
                  {(l.created_at || "").slice(0, 19).replace("T", " ")}
                </td>
                <td className="p-2 font-mono text-[11px]">{l.action_type}</td>
                <td className="p-2 font-mono text-[11px] text-slate-500">{(l.company_id || "-").slice(0, 8)}</td>
                <td className="p-2 font-mono text-[11px] text-slate-500">{(l.user_id || "-").slice(0, 8)}</td>
                <td className="p-2 text-slate-700">{l.description}</td>
              </tr>
            ))}
            {items.length === 0 && !loading && (
              <tr><td colSpan={5} className="p-6 text-center text-slate-500">No entries match.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FieldInline({ label, value, onChange, testId, placeholder, icon: Icon }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">{label}</span>
      <div className="relative mt-1">
        {Icon && <Icon className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />}
        <input data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
               className={`w-full ${Icon ? "pl-7" : "pl-3"} pr-3 py-1.5 text-sm rounded-lg border border-slate-300 focus:border-slate-500 outline-none`} />
      </div>
    </label>
  );
}
